import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, PriceBreakdown } from './CheckoutSummaryElements';

export const VerticalTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center bg-slate-50"
      style={{
        fontFamily: config.fontFamily || 'Inter, sans-serif',
      }}
    >
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden">
        {/* Step Flow Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-6 object-contain" />}
            <span className="text-xs font-bold text-slate-800">1-Step Fast Checkout</span>
          </div>
          <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
            {currencySymbol}{finalTotal.toFixed(2)}
          </span>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          {/* Plan Section */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-3">
            <div className="flex justify-between items-baseline">
              <div>
                <h3 className="text-base font-bold text-slate-900">{plan.tier} Plan</h3>
                {plan.description && <p className="text-xs text-slate-500 mt-0.5">{plan.description}</p>}
              </div>
              <span className="text-xs font-black text-slate-900">{currencySymbol}{plan.price}</span>
            </div>
            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />
            <PlanFeatureList features={plan.features} />
          </div>

          <SocialProofAndFomo config={config} />

          <form onSubmit={onSubmitInfo} className="space-y-4">
            <FormFields config={config} formData={formData} onInputChange={onInputChange} />
            <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
            <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />
            <PriceBreakdown plan={plan} billingCycle={billingCycle} addonsTotal={addonsTotal} discountAmount={discountAmount} finalTotal={finalTotal} currencySymbol={currencySymbol} />

            <button
              type="submit"
              disabled={isCapturing}
              className="w-full py-4 text-xs font-black uppercase tracking-wider text-white shadow transition-all hover:opacity-90 disabled:opacity-50"
              style={{
                backgroundColor: config.primaryColor || '#4f46e5',
                borderRadius: config.buttonRadius || '8px',
              }}
            >
              {isCapturing ? 'Submitting...' : config.buttonText || 'Complete Payment'}
            </button>
          </form>

          <TrustAndSecurityBadge config={config} />
          {config.footerText && <p className="text-[10px] text-slate-400 text-center">{config.footerText}</p>}
        </div>
      </div>
    </div>
  );
};
